import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Code, Lightbulb, Users, Zap, Linkedin, Plus } from 'lucide-react';
import { getImageAssetPath } from '@/lib/imageHelper';

const TeamSection = () => {
  const ddiImagePath = getImageAssetPath('ddi.jpeg');
  const sbrImagePath = getImageAssetPath('sbr.jpeg');

  const teamMembers = [
    {
      name: "Dennis Dittrich",
      role: "",
      specialization: "Strategische Software-Entwicklung",
      image: ddiImagePath,
      bio: "Dennis ist unser Software-Architekt und Entwickler. Er entwickelt langfristige Lösungen und steigt tief in die Materie des Kunden ein, um nachhaltige Software-Architekturen zu schaffen.",
      expertise: [
        "Software-Architektur",
        "Strategische Planung",
        "Kundenanalyse",
        "Langfristige Lösungen",
        "Schnittstellen & Automatisierung",
        "Projektstrukturierung"
      ],
      personality: "Planungsorientiert und strukturiert, blickt über den Tellerrand hinaus und behält Abhängigkeiten sowie Auswirkungen auf anderen Ebenen stets im Blick.",
      social: {
        linkedin: "https://www.linkedin.com/in/dennis-dittrich/"
      }
    },
    {
      name: "Simon Brüggemann",
      role: "",
      specialization: "ERP & Digitalisierung",
      image: sbrImagePath,
      bio: "Simon ist unser ERP- und Digitalisierungsexperte. Als ehemaliger Systemadministrator und IT-Leiter eines mittelständischen Unternehmens kennt er alle Facetten und Herausforderungen mittelständischer Unternehmen aus erster Hand.",
      expertise: [
        "ERP-Systeme & Integration",
        "Microsoft Business Central",
        "Digitalisierungsstrategien",
        "IT-Infrastruktur",
        "Prozessoptimierung",
        "Change Management"
      ],
      personality: "Praxisorientierter Experte mit Hands-On Mentalität, der die Herausforderungen des Mittelstands versteht und pragmatische Lösungen entwickelt.",
      social: {
        linkedin: "https://www.linkedin.com/in/simon-br%C3%BCggemann-b00171176/"
      }
    }
  ];

  return (
    <section id="team" className="py-16 md:py-24 bg-white">
      <div className="container">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Die <span className="text-blue-600">Köpfe</span> hinter den Lösungen
          </h2>
          <p className="text-lg text-gray-600">
            Zwei Gründer, eine Vision: Unternehmen durch erstklassige Software-Entwicklung 
            zum Erfolg zu verhelfen.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {teamMembers.map((member, index) => (
            <Card key={index} className="border-0 shadow-xl bg-white overflow-hidden">
              <CardContent className="p-0">
                {/* Header mit Bild und Grundinfos */}
                <div className="bg-gradient-to-br from-blue-50 to-gray-50 p-8 text-center">
                  <div className="w-32 h-32 mx-auto mb-6 rounded-full overflow-hidden bg-gray-200">
                    <img 
                      src={member.image} 
                      alt={member.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-2xl font-bold mb-2">{member.name}</h3>
                  <p className="text-blue-600 font-semibold mb-2">{member.role}</p>
                  <p className="text-gray-600 text-sm">{member.specialization}</p>
                </div>

                {/* Content */}
                <div className="p-8">
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <Users className="h-4 w-4 mr-2 text-blue-600" />
                      Über {member.name.split(' ')[0]}:
                    </h4>
                    <p className="text-gray-700 leading-relaxed">{member.bio}</p>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <Code className="h-4 w-4 mr-2 text-green-600" />
                      Expertise:
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {member.expertise.map((skill, skillIndex) => (
                        <div key={skillIndex} className="flex items-center text-sm">
                          <span className="text-green-500 mr-2">•</span>
                          <span className="text-gray-600">{skill}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <Lightbulb className="h-4 w-4 mr-2 text-orange-600" />
                      Persönlichkeit:
                    </h4>
                    <p className="text-gray-700 text-sm italic">{member.personality}</p>
                  </div>

                  {/* Social Links */}
                  <div className="flex justify-center space-x-4 pt-4 border-t border-gray-100">
                    <a 
                      href={member.social.linkedin} 
                      className="p-2 text-gray-600 hover:text-blue-600 transition-colors"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Linkedin className="h-5 w-5" />
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Freelancer Section */}
        <div className="mt-16">
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              <span className="text-blue-600">+</span> Freelancer
            </h3>
            <div className="max-w-4xl mx-auto">
              <p className="text-lg text-gray-600 leading-relaxed">
                Wir denken, dass Projektteams mit Experten gewinnbringend und die Zukunft im Arbeitsmarkt sind. 
                Die Herausforderungen gerade in den digitalen Technologien sind so umfassend geworden, dass man 
                Experten für jeden Bereich braucht. Deswegen arbeiten wir mit Freelancern zusammen, um ein 
                perfektes Ergebnis gegenüber unseren Kunden zu vertreten.
              </p>
            </div>
          </div>
        </div>

        {/* Team Philosophy */}
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl shadow-xl p-8 max-w-4xl mx-auto mt-16 text-center">
          <h3 className="text-2xl font-bold mb-4 text-gray-900">
            Warum wir als Team funktionieren
          </h3>
          <p className="text-lg text-gray-600 mb-8">
            Dennis und Simon arbeiten seit Jahren erfolgreich zusammen - diese Vertrautheit spiegelt sich 
            in unserer nahtlosen Zusammenarbeit und gemeinsamen Vision wider.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <Zap className="h-8 w-8 text-blue-600 mx-auto mb-3" />
              <h4 className="font-bold text-gray-900 mb-2">Perfekte Ergänzung</h4>
              <p className="text-gray-700 text-sm">
                Dennis' Software-Architektur + Simons ERP-Expertise = 360° Lösungen
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <Users className="h-8 w-8 text-green-600 mx-auto mb-3" />
              <h4 className="font-bold text-gray-900 mb-2">Gemeinsame Werte</h4>
              <p className="text-gray-700 text-sm">
                Qualität vor Quantität, langfristige Partnerschaften, ehrliche Beratung
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <Code className="h-8 w-8 text-purple-600 mx-auto mb-3" />
              <h4 className="font-bold text-gray-900 mb-2">Technische Exzellenz</h4>
              <p className="text-gray-700 text-sm">
                Moderne Technologien, sauberer Code, zukunftssichere Architekturen
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeamSection; 